import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/lib/auth/auth-provider";

export type TenantRole = "owner" | "admin" | "reviewer" | "viewer";

const WRITE_ROLES: readonly TenantRole[] = ["owner", "admin"];

interface UseRoleResult {
  role: TenantRole | null;
  isLoading: boolean;
  canWrite: boolean;
}

export function useRole(tenantId: string | null | undefined): UseRoleResult {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["tenant-role", tenantId, user?.id],
    queryFn: async (): Promise<TenantRole | null> => {
      if (!tenantId || !user) return null;
      const { data: row, error } = await supabase
        .from("tenant_members")
        .select("role")
        .eq("tenant_id", tenantId)
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw new Error(`useRole: ${error.message}`);
      return (row?.role as TenantRole | undefined) ?? null;
    },
    enabled: !!tenantId && !!user,
  });
  const role = data ?? null;
  const canWrite = role !== null && WRITE_ROLES.includes(role);
  return { role, isLoading, canWrite };
}
